import ExecutionEnvironment from 'exenv';
import queryString from 'query-string';

import { REVIEW_STEP } from 'constants/checkoutFlow';
import { CV_WHITE_LIST } from 'constants/appConstants';
import {
  MAFIA_CLIENT_DESKTOP_PREFIX,
  MAFIA_CLIENT_MOBILE_PREFIX,
  MAFIA_CLIENT_ZPOT_PREFIX
} from 'constants/mafiaClient';
import { NON_STANDARD_SHIP_OPTIONS_MAP } from 'constants/nonStandardShipOptions';
import {
  ADDRESS,
  ADDRESS_ASSOCIATION,
  CART_QUANTITY_LIMIT,
  DELIVERY_INFORMATION_MISSING,
  DELIVERY_OPTION,
  DESTINATION_INFORMATION_MISSING,
  DROP_PRODUCT_NOT_AVAILABLE,
  DROP_PRODUCT_PURCHASE_AUTHORIZATION_MISSING,
  DROP_PRODUCT_QUANTITY_LIMIT,
  EXPIRED_INSTRUMENT,
  FULLFILMENT_NETWORK_MISSING,
  GC_PAYMENT_METHOD_NOT_ALLOWED,
  GC_QUANTITY,
  GIFTCARD_CODE_ALREADY_REDEMEED,
  GIFTCARD_CODE_CANCELLED,
  GIFTCARD_CODE_EXPIRED,
  GIFTCARD_CODE_INVALID,
  INACTIVE_INSTRUMENT,
  INACTIVE_INSTRUMENT_BILLING_ADDRESS_VIOLATION,
  INSUFFICIENT_COVERAGE,
  INVALID_COUPON_BALANCE,
  INVALID_GIFT_OPTION,
  INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM,
  ITEM_QUANTITY_UNAVAILABLE,
  JURISDICTION,
  LEGACY_ORDER_LEVEL_BILLING_ADDRESS_MISSING,
  MISSING_DIGITAL_DELIVERY_INFORMATION,
  OFFER_LISTING_AND_OFFER_SKU_DIFFER,
  OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION,
  PAYMENT_METHOD,
  PAYMENT_PLAN_MISSING,
  PRODUCT_MISSING,
  PROMOTIONAL_CODE_ALREADY_REDEEMED,
  PROMOTIONAL_CODE_BAD,
  PROMOTIONAL_CODE_EXPIRED,
  PROMOTIONAL_CODE_INVALID_FOR_PURCHASE,
  PROMOTIONAL_CODE_USED_BEFORE_START_DATE,
  QUANTITY_LIMITS,
  SHIP_ADDRESS_DEACTIVATED,
  SHIPPING_ENGINE_FILTER_BASED,
  SHIPPING_ENGINE_PROVIDER_BASED,
  SHIPPING_ENGINE_REMOVER_BASED,
  SHIPPING_ENGINE_SHIPPING_OFFERING_NOT_SET,
  VALID_PAYMENT_METHOD_REQUIRED
} from 'constants/constraintViolations';
import {
  ZAPPOS_LWA_PLUS_PRIME,
  ZAPPOS_NEW_VIP,
  ZAPPOS_REWARDS_ELITE,
  ZAPPOS_REWARDS_GOLD,
  ZAPPOS_REWARDS_PLATINUM,
  ZAPPOS_REWARDS_SILVER_AS_GOLD
} from 'constants/shippingBenefitReasons';
import {
  DIGITAL_GC_ONLY_CART,
  MIXED_WITH_BOTH_GC,
  MIXED_WITH_EGC,
  MIXED_WITH_PHYSICAL,
  NON_MIXED_WITH_BOTH_GC,
  PHYSICAL_GC_ONLY_CART,
  RETAIL_ONLY_CART
} from 'constants/cartTypes';
import marketplace from 'cfg/marketplace.json';
import { toUSD } from 'helpers/NumberFormats';
import { toArray } from 'helpers/lodashReplacement';
import { isDesktop } from 'helpers/ClientUtils';

const {
  checkout: { shippingCountriesWhitelist },
  subsiteId: { desktop: desktopSubsiteId, mobile: mobileSubsiteId }
} = marketplace;

export function isTwoDayPrimeShippingPerk(shippingReasonCode) {
  return ZAPPOS_LWA_PLUS_PRIME === shippingReasonCode;
}

export function isOneDayVipShippingPerk(shippingReasonCode) {
  return shippingReasonCode === ZAPPOS_REWARDS_GOLD
  || shippingReasonCode === ZAPPOS_REWARDS_PLATINUM
  || shippingReasonCode === ZAPPOS_REWARDS_ELITE
  || shippingReasonCode === ZAPPOS_REWARDS_SILVER_AS_GOLD
  || shippingReasonCode === ZAPPOS_NEW_VIP;
}

export function buildCheckoutErrorQueryString(params) {
  return `type=martyCheckoutError&${queryString.stringify(params)}`;
}

export function getNonStandardShipOption(promise) {
  return NON_STANDARD_SHIP_OPTIONS_MAP[promise] || promise;
}
export function hasCartQuantityLimit(cvs) {
  return cvs.hasOwnProperty(CART_QUANTITY_LIMIT);
}

export function hasGcQuantity(cvs) {
  return cvs.hasOwnProperty(GC_QUANTITY);
}

export function hasFraudCheckAroundPromoCode(cvs) {
  return cvs.hasOwnProperty(INVALID_COUPON_BALANCE);
}

export function hasExpiredInstrument(cvs) {
  return cvs.hasOwnProperty(EXPIRED_INSTRUMENT);
}

export function hasInactiveInstrument(cvs) {
  return cvs.hasOwnProperty(INACTIVE_INSTRUMENT);
}

export function hasGenericProblemWithPayment(cvs) {
  return cvs.hasOwnProperty(PAYMENT_METHOD);
}

export function hasInvalidShippingOptions(cvs) {
  return cvs.hasOwnProperty(INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM)
    || cvs.hasOwnProperty(SHIPPING_ENGINE_FILTER_BASED)
    || cvs.hasOwnProperty(DELIVERY_OPTION)
    || cvs.hasOwnProperty(SHIPPING_ENGINE_SHIPPING_OFFERING_NOT_SET);
}

export function isShippableAddress(countryCode) {
  return shippingCountriesWhitelist.includes(countryCode);
}

export function needsShippingOptions(cvs) {
  return cvs.hasOwnProperty(DELIVERY_INFORMATION_MISSING);
}

export function isMissingFulfillmentNetwork(cvs) {
  return cvs.hasOwnProperty(FULLFILMENT_NETWORK_MISSING);
}

export function needsValidShippingAddress(cvs) {
  return cvs.hasOwnProperty(DESTINATION_INFORMATION_MISSING)
  || cvs.hasOwnProperty(ADDRESS)
  || cvs.hasOwnProperty(SHIP_ADDRESS_DEACTIVATED)
  || cvs.hasOwnProperty(SHIPPING_ENGINE_REMOVER_BASED);
}

export function hasInvalidGiftOptions(cvs) {
  return cvs.hasOwnProperty(INVALID_GIFT_OPTION);
}

export function isInactiveShippingAddress(cvs) {
  return cvs.hasOwnProperty(SHIP_ADDRESS_DEACTIVATED);
}

export function isGeneralAddressContstraintPresent(cvs) {
  return cvs.hasOwnProperty(ADDRESS);
}

export function isMissingShippingDestination(cvs) {
  return cvs.hasOwnProperty(DESTINATION_INFORMATION_MISSING);
}

export function needsValidPayment(cvs) {
  return cvs.hasOwnProperty(INSUFFICIENT_COVERAGE)
  || cvs.hasOwnProperty(PAYMENT_PLAN_MISSING)
  || cvs.hasOwnProperty(VALID_PAYMENT_METHOD_REQUIRED);
}

export function needsValidBillingAddress(cvs) {
  return cvs.hasOwnProperty(INACTIVE_INSTRUMENT_BILLING_ADDRESS_VIOLATION) ||
    cvs.hasOwnProperty(LEGACY_ORDER_LEVEL_BILLING_ADDRESS_MISSING);
}

export function needsToReAssociatePaymentToAddress(cvs) {
  return cvs.hasOwnProperty(ADDRESS_ASSOCIATION);
}

export function needsToReAssociatePaymentToAddressForPayment(cvs, paymentInstrumentId) {
  return needsToReAssociatePaymentToAddress(cvs) && cvs[ADDRESS_ASSOCIATION].paymentInstrumentId === paymentInstrumentId;
}

export function needsDigitalDeliveryInformation(cvs) {
  return cvs.hasOwnProperty(MISSING_DIGITAL_DELIVERY_INFORMATION);
}

export function hasAmazonCatalogIssue(cvs) {
  return cvs.hasOwnProperty(OFFER_LISTING_AND_OFFER_SKU_DIFFER);
}

export function hasProblemWithItemInCart(cvs) {
  return cvs.hasOwnProperty(JURISDICTION)
    || cvs.hasOwnProperty(ITEM_QUANTITY_UNAVAILABLE)
    || cvs.hasOwnProperty(OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION)
    || cvs.hasOwnProperty(QUANTITY_LIMITS)
    || cvs.hasOwnProperty(SHIPPING_ENGINE_PROVIDER_BASED)
    || cvs.hasOwnProperty(GC_QUANTITY)
    || cvs.hasOwnProperty(DROP_PRODUCT_NOT_AVAILABLE)
    || cvs.hasOwnProperty(DROP_PRODUCT_PURCHASE_AUTHORIZATION_MISSING)
    || cvs.hasOwnProperty(DROP_PRODUCT_QUANTITY_LIMIT)
    || isMissingFulfillmentNetwork(cvs);
}

export function isMissingProducts(cvs) {
  return cvs.hasOwnProperty(PRODUCT_MISSING);
}

export function hasAsinLevelProblem(cvs) {
  return hasProblemWithItemInCart(cvs) || hasInvalidShippingOptions(cvs);
}

export function isTryingToBuyGiftCardWithGiftCard(cvs) {
  return cvs.hasOwnProperty(GC_PAYMENT_METHOD_NOT_ALLOWED);
}

export function isPlacingOrderBlocked(cvs) {
  return (cvs ? toArray(cvs) : []).some(cv => !CV_WHITE_LIST.includes(cv.name));
}

export function canPurchaseHavePromos(cartType) {
  return cartType === MIXED_WITH_EGC
  || cartType === MIXED_WITH_PHYSICAL
  || cartType === MIXED_WITH_BOTH_GC
  || cartType === RETAIL_ONLY_CART;
}

export function isDigitalDeliveryOnlyCart(cartType) {
  return cartType === DIGITAL_GC_ONLY_CART;
}

export function isDigitalCart(cartType) {
  return cartType === DIGITAL_GC_ONLY_CART
  || cartType === MIXED_WITH_EGC
  || cartType === MIXED_WITH_BOTH_GC
  || cartType === NON_MIXED_WITH_BOTH_GC;
}

export function isNonRetailOnlyCart(cartType) {
  return cartType !== RETAIL_ONLY_CART;
}

export function isGiftCardOnlyCart(cartType) {
  return cartType === PHYSICAL_GC_ONLY_CART
  || cartType === DIGITAL_GC_ONLY_CART
  || cartType === NON_MIXED_WITH_BOTH_GC;
}

export function isDigitalChallenge(cvs) {
  return cvs.hasOwnProperty(ADDRESS_ASSOCIATION) && cvs[ADDRESS_ASSOCIATION].challengeType === 'DigitalChallenge';
}

export function refetchShipOptionsBasedOnCheckoutData(data) {
  return checkoutDataHasShipEngineFilterBaseCV(data) || checkoutDataHasShipEngineRemoverBaseCV(data);
}

export function checkoutDataHasShipEngineFilterBaseCV(data) {
  const { purchaseStatus: { constraintViolations: cvs } } = data;
  return !!cvs.find(cv => cv.name === SHIPPING_ENGINE_FILTER_BASED);
}

export function checkoutDataHasShipEngineRemoverBaseCV(data) {
  const { purchaseStatus: { constraintViolations: cvs } } = data;
  return !!cvs.find(cv => cv.name === SHIPPING_ENGINE_REMOVER_BASED);
}

export function hasBadPromoCodeOrGiftCardFromCVList(cvs) {
  const constraintViolations = {};
  cvs.forEach(cv => {
    constraintViolations[cv.name] = cv;
  });
  return hasBadPromoCodeOrGiftCard(constraintViolations);
}

export function hasBadPromoCodeOrGiftCard(cvs) {
  return cvs.hasOwnProperty(PROMOTIONAL_CODE_ALREADY_REDEEMED)
    || cvs.hasOwnProperty(PROMOTIONAL_CODE_EXPIRED)
    || cvs.hasOwnProperty(PROMOTIONAL_CODE_USED_BEFORE_START_DATE)
    || cvs.hasOwnProperty(PROMOTIONAL_CODE_INVALID_FOR_PURCHASE)
    || cvs.hasOwnProperty(PROMOTIONAL_CODE_BAD)
    // must be added in, but can't until this is complete: https://jira.zappos.net/browse/MAFIA-1132
    // || cvs.hasOwnProperty(GC_PAYMENT_METHOD_NOT_ALLOWED)
    || cvs.hasOwnProperty(GIFTCARD_CODE_ALREADY_REDEMEED)
    || cvs.hasOwnProperty(GIFTCARD_CODE_CANCELLED)
    || cvs.hasOwnProperty(GIFTCARD_CODE_EXPIRED)
    || cvs.hasOwnProperty(GIFTCARD_CODE_INVALID);
}

export function isReadyToSubmitCheckout({ constraintViolations, currentStep }) {
  const isOnReviewStep = currentStep === REVIEW_STEP;
  const isBlockedByCVs = isPlacingOrderBlocked(constraintViolations);
  return isOnReviewStep && !isBlockedByCVs;
}

export function hasOutOfStockCV(cvs) {
  if (!cvs || !cvs.length) {
    return false;
  }

  return !!cvs.find(cv => cv.name === ITEM_QUANTITY_UNAVAILABLE || cv.name === OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION);
}

export const inIframe = win => {
  if (typeof win === 'undefined' && typeof window === 'undefined') {
    return false;
  }

  const w = win || window;

  try {
    return w.self !== w.top;
  } catch (e) {
    return true;
  }
};

export const formatShippingPrice = price => {
  if (typeof price === 'undefined') {
    return '';
  }
  return price === 0 ? '- FREE' : `- ${toUSD(price)}`;
};

export const getMafiaClientHeaderPrefix = (params = {}) => {
  const { isClient = ExecutionEnvironment.canUseDOM, win = window } = params;

  if (!isClient) {
    return;
  }

  const isDesktopDevice = isDesktop(win);

  switch (true) {
    case inIframe(win):
      return MAFIA_CLIENT_ZPOT_PREFIX;
    case isDesktopDevice:
      return MAFIA_CLIENT_DESKTOP_PREFIX;
    default:
      return MAFIA_CLIENT_MOBILE_PREFIX;
  }
};

export const buildPaginationRange = (currentPage, numPages) => {
  const current = currentPage,
    delta = 2,
    last = numPages,
    left = current - delta,
    rangeWithDots = [],
    right = current + delta + 1;

  let l;

  for (let i = 1; i <= last; i++) {
    if (i === 1 || i === last || i >= left && i < right) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }
  }

  return rangeWithDots;
};

export function buildOrderConfirmationEventData(params) {
  const subsiteId = isDesktop() ? desktopSubsiteId : mobileSubsiteId;

  const {
    orderId: amazonPhysicalOrderId,
    digitalOrderId: amazonDigitalOrderId,
    purchaseId: amazonPurchaseId,
    chargeSummary: { shippingCharge, estimatedTax, subTotal, total },
    productList
  } = params;

  const orderItem = productList.map(item => {
    const { productId, stockId, asin } = item;
    return { asin, productId, stockId };
  });

  const checkout = {
    amazonPurchaseId,
    amazonDigitalOrderId,
    amazonPhysicalOrderId,
    shipping: +shippingCharge.toFixed(2),
    tax: +estimatedTax.toFixed(2),
    subtotal: +subTotal.toFixed(2),
    total: +total.toFixed(2),
    orderItem
  };

  return {
    type: 'Checkout',
    checkout,
    subsiteId
  };
}
